import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, X, ChevronsUpDown, Check, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useSiteSettings } from "@/hooks/use-site-settings";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TESTAMENTS = [
  { key: "all", label: "All Scripture" },
  { key: "OT", label: "Old Testament" },
  { key: "NT", label: "New Testament" },
];

const triggerSize = (size, normal, small) => (size === "full" ? "h-11 w-full rounded-full bg-card text-sm" : size === "compact" ? `h-8 ${small} rounded-full bg-card text-xs` : `${normal} rounded-full bg-card`);

function BookPicker({ books, value, onChange, size }) {
  const [open, setOpen] = useState(false);
  const selected = books.find((b) => b.name === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid="filter-select-book"
          className={`flex items-center justify-between rounded-full border border-input bg-card px-3 ${size === "full" ? "h-11 w-full text-sm" : size === "compact" ? "h-8 w-[150px] text-xs" : "h-10 w-[200px] text-sm"}`}
        >
          <span className="truncate">{selected ? `${selected.name} (${selected.count})` : "All Books"}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Type a book name…" data-testid="filter-book-search" />
          <CommandList className="max-h-72">
            <CommandEmpty>No book found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="All Books" onSelect={() => { onChange("all"); setOpen(false); }} data-testid="filter-book-option-all">
                <Check className={`mr-2 h-4 w-4 ${value === "all" ? "opacity-100" : "opacity-0"}`} /> All Books
              </CommandItem>
              {books.map((b) => (
                <CommandItem key={b.name} value={b.name} onSelect={() => { onChange(b.name); setOpen(false); }} data-testid={`filter-book-option-${b.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === b.name ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1">{b.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{b.count}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const countActive = (f) => ["testament", "who_group", "theme", "book", "type"].filter((k) => f[k] !== "all").length;

function TestamentPills({ filters, setFilter, size }) {
  return (
    <div className={`flex items-center gap-2 ${size === "full" ? "" : "flex-wrap"}`}>
      {TESTAMENTS.map((t) => (
        <motion.button
          key={t.key}
          whileTap={{ scale: 0.96 }}
          data-testid={`filter-pill-testament-${t.key.toLowerCase()}`}
          onClick={() => setFilter("testament", t.key)}
          className={`relative rounded-full border font-medium transition-colors duration-200 ${size === "compact" ? "px-3 py-1 text-xs" : size === "full" ? "flex-1 whitespace-nowrap px-1 py-2 text-[13px]" : "px-4 py-1.5 text-sm"} ${
            filters.testament === t.key ? "border-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
          }`}
        >
          {filters.testament === t.key && (
            <motion.span layoutId={`activeTestamentPill-${size}`} className="absolute inset-0 -z-10 rounded-full bg-primary shadow-sm" transition={{ type: "spring", stiffness: 400, damping: 30 }} />
          )}
          {t.label}
        </motion.button>
      ))}
    </div>
  );
}

function FilterSelects({ meta, filters, setFilter, books, themes, types, size }) {
  const showThemes = useSiteSettings().settings.explore_themes;
  return (
    <>
      <Select value={filters.who_group} onValueChange={(v) => setFilter("who_group", v)}>
        <SelectTrigger data-testid="filter-select-who" className={triggerSize(size, "w-[190px]", "w-[150px]")}><SelectValue placeholder="Voice Group" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Voice Group</SelectItem>
          {(meta?.who_groups || []).map((g) => <SelectItem key={g.key} value={g.key} data-testid={`filter-who-option-${g.key}`}>{g.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {showThemes && (
        <Select value={filters.theme} onValueChange={(v) => setFilter("theme", v)}>
          <SelectTrigger data-testid="filter-select-theme" className={triggerSize(size, "w-[190px]", "w-[150px]")}><SelectValue placeholder="Theme" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Themes</SelectItem>
            {themes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Select value={filters.type} onValueChange={(v) => setFilter("type", v)}>
        <SelectTrigger data-testid="filter-select-type" className={triggerSize(size, "w-[170px]", "w-[130px]")}><SelectValue placeholder="Question type" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>
      <BookPicker books={books} value={filters.book} onChange={(v) => setFilter("book", v)} size={size} />
    </>
  );
}

const plural = (n) => `${n ?? "…"} ${n === 1 ? "question" : "questions"}`;

function SearchInput({ filters, setFilter, compact }) {
  return (
    <div className="relative min-w-0 flex-1">
      <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
      <input
        data-testid="search-input-field"
        value={filters.search}
        onChange={(e) => setFilter("search", e.target.value)}
        placeholder="Search questions, verses, commentary, people…"
        className={`w-full rounded-full border border-border bg-card pl-12 pr-4 text-foreground shadow-sm outline-none transition-all placeholder:text-muted-foreground focus:border-primary ${compact ? "py-2 text-sm" : "py-3.5 text-base"}`}
      />
    </div>
  );
}

export default function FiltersBar(props) {
  const { meta, filters, setFilter, resetFilters, resultCount, compact = false } = props;
  const mobile = useIsMobile();
  const books = useMemo(() => (meta?.books || []).filter((b) => filters.testament === "all" || b.testament === filters.testament), [meta, filters.testament]);
  const themes = useMemo(() => (meta?.themes || []).filter(Boolean), [meta]);
  const types = useMemo(() => (meta?.types || []).filter(Boolean), [meta]);
  useEffect(() => {
    if (filters.book !== "all" && !books.some((b) => b.name === filters.book)) setFilter("book", "all");
  }, [filters.testament]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: only reset the book when the testament changes
  const activeCount = countActive(filters);
  const lists = { meta, filters, setFilter, books, themes, types };

  if (mobile) return <MobileFilters {...lists} resetFilters={resetFilters} resultCount={resultCount} activeCount={activeCount} />;

  return (
    <div className={compact ? "space-y-2" : "space-y-4"} data-compact={compact ? "1" : "0"}>
      <SearchInput filters={filters} setFilter={setFilter} compact={compact} />
      <div className={compact ? "flex flex-wrap items-center gap-2" : "space-y-2"}>
        <TestamentPills filters={filters} setFilter={setFilter} size={compact ? "compact" : "normal"} />
        <div className={`flex flex-1 flex-wrap items-center ${compact ? "gap-2" : "gap-3"}`}>
          <FilterSelects {...lists} size={compact ? "compact" : "normal"} />
          {activeCount > 0 && (
            <button data-testid="clear-filters-button" onClick={resetFilters}
              className="flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-destructive">
              <X className="h-4 w-4" /> Clear
            </button>
          )}
          <span className="ml-auto font-mono text-xs text-muted-foreground" data-testid="result-count">{plural(resultCount)}</span>
        </div>
      </div>
    </div>
  );
}

function activeChips(meta, filters) {
  const who = (meta?.who_groups || []).find((g) => g.key === filters.who_group)?.label;
  return [
    ["testament", TESTAMENTS.find((t) => t.key === filters.testament)?.label],
    ["who_group", who || filters.who_group],
    ["theme", filters.theme],
    ["type", filters.type],
    ["book", filters.book],
  ].filter(([k]) => filters[k] !== "all");
}

function MobileFilters({ resetFilters, resultCount, activeCount, ...lists }) {
  const { meta, filters, setFilter } = lists;
  const [open, setOpen] = useState(false);
  const chips = activeChips(meta, filters);
  return (
    <div className="space-y-2" data-testid="mobile-filters">
      <div className="flex items-center gap-2">
        <SearchInput filters={filters} setFilter={setFilter} compact />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button type="button" data-testid="mobile-filters-button"
              className={`flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-4 text-sm font-medium transition-colors ${activeCount ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground"}`}>
              <SlidersHorizontal className="h-4 w-4" /> Filters{activeCount > 0 && <span data-testid="mobile-filters-count"> · {activeCount}</span>}
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl pb-8" data-testid="mobile-filters-sheet">
            <SheetHeader><SheetTitle className="font-serif text-2xl">Filter questions</SheetTitle></SheetHeader>
            <div className="mt-5 space-y-3">
              <TestamentPills filters={filters} setFilter={setFilter} size="full" />
              <FilterSelects {...lists} size="full" />
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button type="button" onClick={resetFilters} disabled={!activeCount} data-testid="clear-filters-button"
                className="h-11 rounded-full px-5 text-sm font-medium text-muted-foreground transition-colors hover:text-destructive disabled:opacity-40">Clear all</button>
              <button type="button" onClick={() => setOpen(false)} data-testid="mobile-filters-apply"
                className="h-11 flex-1 rounded-full bg-primary text-sm font-semibold text-primary-foreground">Show {plural(resultCount)}</button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <div className="flex items-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none]">
        {chips.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k, "all")} data-testid={`mobile-chip-${k}`}
            className="flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
            {label} <X className="h-3 w-3" />
          </button>
        ))}
        <span className="ml-auto shrink-0 font-mono text-[11px] text-muted-foreground" data-testid="result-count">{plural(resultCount)}</span>
      </div>
    </div>
  );
}
